import React, { Fragment } from 'react';
import PropTypes, { arrayOf, func, shape } from 'prop-types';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import EventIcon from '@mui/icons-material/Event';
import * as R from 'ramda';

// utils
import { useUserRolePermissions } from 'hooks';
import { AUTH_SEQUENCING_ALL_DONE } from 'feature/loading-management/utils/const';

// Components
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Divider,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import EmptyState from './EmptyState';
import { withBottomPaddingStyles, withLeftPaddingStyles } from '../EditPanel.styles';

const Authorizations = props => {
  const {
    authorizations,
    unsequencedAuthorizations,
    badgeProps,
    onAddFutureSeq,
    onRemoveSeq,
    sequencedAuthorizations,
    futureSequencedAuthorizations,
  } = props;
  const { isProductLoadingManage } = useUserRolePermissions();
  const { palette } = useTheme();
  const theme = useTheme();

  const sxStyles = {
    buttonLabel: {
      fontSize: 32,
      height: '100%',
      margin: 'auto',
      textAlign: 'center',
    },
    iconContainer: {
      ':disabled': {
        border: `1px solid ${palette.text.disabled}`,
      },
      borderRadius: 0.5,
      boxShadow: 'none',
      minWidth: 34,
      padding: 0,
      width: 34,
    },
  };

  const shortNameSort = R.sortWith([
    R.descend(R.prop('supplierDirectedBlend')),
    R.ascend(R.path(['inventoryOwner', 'nameShort'])),
  ]);

  return (
    <Box color="primary.dark" pt={3} px={3}>
      <Typography color="inherit" sx={withBottomPaddingStyles(theme)} variant="subtitle1">
        Available Authorizations
      </Typography>
      <Divider />
      {!authorizations.length ? (
        <Box pt={1.5}>
          <EmptyState
            icon={<DoneAllRoundedIcon sx={{ fontSize: 32 }} />}
            text={AUTH_SEQUENCING_ALL_DONE}
            type="DONE_ALL_ROUNDED"
          />
        </Box>
      ) : (
        shortNameSort(authorizations).map(authorization => {
          const { active, inventoryOwner, productAuthorizationId, supplierDirectedBlend } =
            authorization;
          const currentBadgeProps = badgeProps(active, inventoryOwner, supplierDirectedBlend);
          return (
            <Fragment
              key={`authorization-${productAuthorizationId}${supplierDirectedBlend ? '-SDB' : ''}`}
            >
              <Box alignItems="center" display="flex" justifyContent="space-between" py={1.5}>
                <Box display="flex" justifyContent="space-between">
                  <Tooltip
                    enterTouchDelay={10}
                    {...currentBadgeProps?.tooltipProps}
                    title={currentBadgeProps?.title}
                  >
                    <Chip
                      label={currentBadgeProps?.tag}
                      type={currentBadgeProps?.type}
                      variant="badge"
                    />
                  </Tooltip>
                  <Typography color="textPrimary" sx={withLeftPaddingStyles(theme)} variant="body2">
                    {supplierDirectedBlend ? 'Supplier Directed Blend' : inventoryOwner.nameShort}
                  </Typography>
                </Box>
                {isProductLoadingManage && (
                  <ButtonGroup>
                    <Button
                      disabled={sequencedAuthorizations.includes(authorization)}
                      onClick={() => onRemoveSeq(unsequencedAuthorizations.indexOf(authorization))}
                      sx={sxStyles.iconContainer}
                      title="Add to Current Sequence"
                      variant="outlined"
                    >
                      <AddCircleOutlineRoundedIcon />
                    </Button>
                    <Button
                      disabled={futureSequencedAuthorizations.includes(productAuthorizationId)}
                      onClick={() => {
                        onAddFutureSeq(authorization);
                      }}
                      sx={sxStyles.iconContainer}
                      title="Add to Future Sequence"
                      variant="outlined"
                    >
                      <EventIcon />
                    </Button>
                  </ButtonGroup>
                )}
              </Box>
              <Divider />
            </Fragment>
          );
        })
      )}
    </Box>
  );
};

export default Authorizations;

Authorizations.propTypes = {
  authorizations: arrayOf(shape({})),
  badgeProps: func.isRequired,
  futureSequencedAuthorizations: arrayOf(PropTypes.number),
  onAddFutureSeq: func.isRequired,
  onRemoveSeq: func.isRequired,
  sequencedAuthorizations: arrayOf(shape({})),
  unsequencedAuthorizations: arrayOf(shape({})),
};

Authorizations.defaultProps = {
  authorizations: [],
  futureSequencedAuthorizations: [],
  sequencedAuthorizations: [],
  unsequencedAuthorizations: [],
};
